import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Session, Message, Department, Sentiment } from '../types/communication.types'
import { useMessages } from '../hooks/useMessages'
import { ImageUploadButton } from './ImageUploadButton'
import { MessageImage } from './MessageImage'
import { VoiceInputButton } from './VoiceInputButton'
// import VoiceTestButton from './VoiceTestButton'
import LocalVoiceRecorder from './LocalVoiceRecorder'
import { AISuggestionBox } from './AISuggestionBox'
import { ConciergeModeBadge } from './ConciergeModeBadge'
import { KTSVG } from '../../../../../_metronic/helpers'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface MessageViewProps {
  session: Session | null
  onClose: () => void
  // Novos props para identificação do agent
  agentId: string          // ID do agent logado
  agentEmail: string       // Email do agent
  agentName: string        // Nome do agent
  hotelId: number          // ID do hotel
  // 🎯 Novo prop para filtro de departamento selecionado
  selectedDepartment?: Department | null
  // 🤖 Props de IA (MÚLTIPLAS SUGESTÕES)
  aiSuggestions?: Array<{
    id: string
    sessionKey: string
    suggestion: string
    sentiment: Sentiment
    originalPrompt: string
    timestamp: number
    messageItems?: Array<{ message: string; imageUrl?: string }>
    imageUrls?: string[]
  }>
  isLoadingAISuggestion?: boolean
  onAcceptAISuggestion?: (suggestionId: string, sessionKey: string, text: string, messageItems?: Array<{ message: string; imageUrl?: string }>) => void
  onDiscardAISuggestion?: (suggestionId: string) => void
}

const MessageView: React.FC<MessageViewProps> = ({ 
  session, 
  onClose, 
  agentId,
  agentEmail,
  agentName,
  hotelId,
  selectedDepartment, // 🎯 Departamento selecionado no filtro
  // 🤖 Props de IA
  aiSuggestions = [],
  isLoadingAISuggestion = false,
  onAcceptAISuggestion,
  onDiscardAISuggestion
}) => {
  const [messageText, setMessageText] = useState('')
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const autoMarkTimerRef = useRef<NodeJS.Timeout | null>(null)
  
  // 🔍 Debug: Log do departamento selecionado
  useEffect(() => {
    console.log('🎯 [MessageView] selectedDepartment mudou:', selectedDepartment);
  }, [selectedDepartment]);
  
  // Hook para gerenciar mensagens
  const {
    messages,
    isLoadingMessages,
    messagesError,
    hasMoreMessages,
    loadMoreMessages,
    sendMessage,
    uploadImage,
    markAsRead,
    isSending,
    isUploading
  } = useMessages({
    session,
    autoMarkAsRead: false, // ❌ Desabilitado auto-mark para permitir unreadCount funcionar
    agentData: {
      agentId,
      agentEmail,
      agentName,
      hotelId
    },
    // PASSAR O CANAL DO FILTRO para envio de mensagens
    channelOverride: selectedDepartment || 'recepcao',
    onNewMessage: (message) => {
      console.log('Nova mensagem recebida no MessageView:', message)
    }
  })
  
  // FILTRAR MENSAGENS POR DEPARTAMENTO SELECIONADO
  // Se houver departamento selecionado, mostrar apenas mensagens daquele canal
  const filteredMessages = React.useMemo(() => {
    if (!selectedDepartment) {
      // Sem filtro: mostrar todas as mensagens
      return messages
    }
    
    // Com filtro: mostrar apenas mensagens do canal selecionado
    const filtered = messages.filter(msg => msg.channel === selectedDepartment)
    
    console.log('[MessageView] Filtrando mensagens:', {
      departamentoSelecionado: selectedDepartment,
      totalMensagens: messages.length,
      mensagensFiltradas: filtered.length
    })
    
    return filtered
  }, [messages, selectedDepartment])
  // Auto-scroll para a última mensagem - sempre no final
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
    }
  }, [])
  
  // Scroll ao receber novas mensagens
  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])
  
  // Scroll ao mudar de sessão
  useEffect(() => {
    scrollToBottom()
  }, [session?.sessionKey, scrollToBottom])
  
  // Marcar como lida após 5 segundos de visualização da sessão
  useEffect(() => {
    if (session && session.unreadCount && session.unreadCount > 0) {
      console.log(`Iniciando timer de 5s para marcar ${session.guestName} como lida...`);
      
      // Cancelar timer anterior se existir
      if (autoMarkTimerRef.current) {
        clearTimeout(autoMarkTimerRef.current);
      }
      
      // Marcar como lida após 5 segundos
      autoMarkTimerRef.current = setTimeout(() => {
        console.log(`Marcando ${session.guestName} como lida após 5s de visualização`);
        markAsRead();
        autoMarkTimerRef.current = null;
      }, 5000);
    }
    
    // Cleanup no unmount
    return () => {
      if (autoMarkTimerRef.current) {
        clearTimeout(autoMarkTimerRef.current);
        autoMarkTimerRef.current = null;
      }
    };
  }, [session, markAsRead])
  
  // Enviar mensagem
  const handleSendMessage = useCallback(async () => {
    if (!messageText.trim() || !session || isSending) return
    
    console.log('🚀 MessageView: Enviando mensagem')
    console.log('📨 Dados da mensagem:', { messageText, session: session.sessionKey })
    console.log('👤 Dados do agente:', { agentId, agentEmail, agentName, hotelId })
    
    try {
      // ✅ ENVIAR VIA API BIDIRECIONAL
      // A API /bidirectional já salva no banco E envia via WebSocket
      await sendMessage(messageText)
      
      console.log('✅ Mensagem enviada com sucesso')
      
      // Limpar campo
      setMessageText('')
      
    } catch (error) {
      console.error('❌ Erro ao enviar mensagem:', error)
    }
  }, [session, messageText, isSending, sendMessage, agentId, agentEmail, agentName, hotelId])
  
  // Upload de imagem
  const handleImageUpload = async (file: File) => {
    if (!session || isUploading) return

    try {
      console.log('📷 Iniciando upload de imagem:', file.name)
      
      // Fazer upload da imagem
      const imageUrl = await uploadImage(file)
      console.log('✅ Upload concluído, URL:', imageUrl)
      
      // Enviar mensagem com imagem
      const imageMessage = `🖼️ Imagem enviada`
      
      console.log('📤 Enviando mensagem com imagem:', { 
        text: imageMessage, 
        imageUrl,
        sessionId: session.id,
        sessionKey: session.sessionKey 
      })
      
      // ✅ ENVIAR VIA API BIDIRECIONAL
      // A API /bidirectional já salva no banco E envia via WebSocket
      await sendMessage(imageMessage, imageUrl)
      
      console.log('✅ Mensagem com imagem enviada com sucesso')
      
    } catch (error) {
      console.error('❌ Erro ao enviar imagem:', error)
      alert(`Erro ao enviar imagem: ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  // Voice input handler com comandos especiais (ESTÁVEL - sem dependências que mudam)
  const handleVoiceTranscript = useCallback((transcript: string) => {
    console.log('Texto reconhecido por voz:', transcript)
    
    if (!transcript?.trim()) {
      console.warn('Transcript inválido ou vazio')
      return
    }

    const cleanTranscript = transcript.trim()
    const lowerTranscript = cleanTranscript.toLowerCase()
    
    console.log('Analisando comando:', lowerTranscript)
    
    // COMANDO: APAGAR TEXTO
    if (lowerTranscript.includes('apagar texto')) {
      console.log('COMANDO: APAGAR TEXTO')
      setMessageText('')
      console.log('TEXTO APAGADO!')
      return
    }
    
    // COMANDO: ENVIAR MENSAGEM
    if (lowerTranscript.includes('enviar texto')) {
      console.log('COMANDO: ENVIAR MENSAGEM')
      
      // Capturar texto atual do input diretamente do DOM
      const inputElement = document.querySelector('textarea[placeholder*="Responder"]') as HTMLTextAreaElement
      const currentInputText = inputElement?.value || ''
      const textToSend = currentInputText.trim()
      
      console.log('Estado atual:', {
        textToSend: `"${textToSend}"`,
        temTexto: !!textToSend
      })
      
      if (textToSend) {
        console.log('ENVIANDO MENSAGEM POR VOZ:', textToSend)
        // Trigger do botão enviar diretamente
        const sendButton = document.querySelector('button[title="Enviar mensagem"]') as HTMLButtonElement
        if (sendButton && !sendButton.disabled) {
          sendButton.click()
        }
      } else {
        console.log('Sem texto para enviar')
      }
      return
    }
    
    // TEXTO NORMAL - ADICIONAR AO INPUT
    console.log('TEXTO NORMAL:', cleanTranscript)
    
    // Adicionar texto reconhecido ao campo atual
    setMessageText(prev => {
      const currentText = prev.trim()
      return currentText ? `${currentText} ${cleanTranscript}` : cleanTranscript
    })
  }, []) // SEM DEPENDÊNCIAS para evitar re-inicializações

  // Handle Enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }
  
  // Carregar mensagens mais antigas
  const handleLoadMore = () => {
    if (hasMoreMessages && !isLoadingMessages) {
      loadMoreMessages()
    }
  }
  
  // Detectar scroll para carregar mais mensagens
  const handleScroll = () => {
    const container = messagesContainerRef.current
    if (!container) return
    
    // Se chegou no topo e tem mais mensagens
    if (container.scrollTop === 0 && hasMoreMessages && !isLoadingMessages) {
      loadMoreMessages()
    }
  }
  
  if (!session) {
    return (
      <div className='card h-100'>
        <div className='card-body d-flex align-items-center justify-content-center text-muted'>
          <div className='text-center'>
            <KTSVG path='/media/icons/duotune/communication/com010.svg' className='svg-icon-3x mb-5' />
            <h4>Selecione uma sessão</h4>
            <p>Escolha uma sessão da lista para visualizar e responder mensagens</p>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className='card h-100 d-flex flex-column'>
      {/* Header removido - agora está no CommunicationCenter.tsx para ganhar espaço */}
      
      {/* Lista de mensagens */}
      <div className='card-body p-0 position-relative' style={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {messagesError && (
          <div className='alert alert-danger mx-8 mt-6'>
            <div className='alert-text'>{messagesError}</div>
          </div>
        )}
        
        <div 
          ref={messagesContainerRef}
          className='px-8 py-4'
          onScroll={handleScroll}
          style={{ 
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Botão carregar mais mensagens */}
          {hasMoreMessages && (
            <div className='text-center py-4'>
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={handleLoadMore}
                disabled={isLoadingMessages}
              >
                {isLoadingMessages ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2'></span>
                    Carregando...
                  </>
                ) : (
                  'Carregar mensagens anteriores'
                )}
              </button>
            </div>
          )}
          
          {/* Loading inicial */}
          {isLoadingMessages && filteredMessages.length === 0 && (
            <div className='text-center py-10'>
              <span className='spinner-border spinner-border-lg'></span>
              <div className='text-muted mt-3'>Carregando mensagens...</div>
            </div>
          )}
          
          {/* Mensagens */}
          {filteredMessages.map((message, index) => (
            <MessageBubble 
              key={message.id} 
              message={message} 
              isAgent={message.author === 'agent'}
              hotelId={hotelId}
              agentName={agentName}
              roomNumber={session.room}
              showTime={
                index === 0 || 
                new Date(message.created).getTime() - new Date(messages[index - 1].created).getTime() > 300000 // 5 min
              }
            />
          ))}
          
          {/* Sem mensagens */}
          {!isLoadingMessages && messages.length === 0 && (
            <div className='text-center py-10 text-muted'>
              <KTSVG path='/media/icons/duotune/communication/com012.svg' className='svg-icon-3x mb-5' />
              <h5>Nenhuma mensagem ainda</h5>
              <p>Seja o primeiro a enviar uma mensagem para {session.guestName}</p>
            </div>
          )}
        </div>
      </div>
      
      {/* 🤖 Sugestões de IA (modo assistido) - MÚLTIPLAS */}
      {aiSuggestions && aiSuggestions.length > 0 && session && (
        <div className='px-6 pt-4'>
          {/* 🔔 Indicador visual de sugestões pendentes */}
          {aiSuggestions.filter(s => s.sessionKey === session.sessionKey).length > 0 && (
            <div className='alert alert-info d-flex align-items-center mb-4 animate__animated animate__pulse animate__infinite' 
                 style={{ borderLeft: '4px solid #0dcaf0' }}>
              <KTSVG 
                path='/media/icons/duotune/general/gen045.svg' 
                className='svg-icon-2x svg-icon-info me-3' 
              />
              <div className='flex-grow-1'>
                <strong>🤖 {aiSuggestions.filter(s => s.sessionKey === session.sessionKey).length} Sugestão(ões) da IA disponível(eis)</strong>
                <p className='mb-0 text-muted small'>
                  Modo Assistido ativo. Revise, edite ou aceite as sugestões abaixo.
                </p>
              </div>
            </div>
          )}
          
          {/* Renderizar todas as sugestões da sessão atual */}
          {aiSuggestions
            .filter(s => s.sessionKey === session.sessionKey)
            .sort((a, b) => a.timestamp - b.timestamp) // Mais antigas primeiro
            .map((suggestion) => (
              <div key={suggestion.id} className='mb-4'>
                <AISuggestionBox
                  suggestion={suggestion.suggestion}
                  messageItems={(suggestion as any).messageItems} // ✅ NOVO: Passar array com message + imageUrl
                  imageUrls={(suggestion as any).imageUrls} // ✅ NOVO: Passar array de URLs
                  sentiment={suggestion.sentiment}
                  isLoading={false}
                  onAccept={(text) => onAcceptAISuggestion?.(suggestion.id, suggestion.sessionKey, text, (suggestion as any).messageItems)}
                  onDiscard={() => onDiscardAISuggestion?.(suggestion.id)}
                  onEdit={(text) => onAcceptAISuggestion?.(suggestion.id, suggestion.sessionKey, text, (suggestion as any).messageItems)}
                />
              </div>
            ))
          }
        </div>
      )}

      {/* Input de envio */}
      <div className='card-footer pt-4'>
        <div className='d-flex'>
          <div className='position-relative flex-grow-1 me-3'>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Responder para ${session.guestName}...`}
              className='form-control border-0 p-3'
              rows={2}
              style={{ resize: 'none' }}
              disabled={isSending}
            />
          </div>
          
          <div 
            className='d-flex flex-row align-items-end gap-2'
            style={{ flexDirection: 'row', alignItems: 'flex-end' }}
          >
            {/* <VoiceInputButton
              onTranscript={handleVoiceTranscript}
              disabled={isSending || isUploading}
            /> */}
            
            <LocalVoiceRecorder
              onTranscript={handleVoiceTranscript}
              disabled={isSending || isUploading}
            />
            
            <ImageUploadButton
              onImageSelect={handleImageUpload}
              disabled={isSending}
              isUploading={isUploading}
            />
            
            <button
              type='button'
              className='btn btn-primary btn-sm d-flex align-items-center'
              onClick={handleSendMessage}
              disabled={!messageText.trim() || isSending}
              title='Enviar mensagem'
              style={{ whiteSpace: 'nowrap', minHeight: '31px' }}
            >
              {isSending ? (
                <span className='spinner-border spinner-border-sm'></span>
              ) : (
                <>
                  <i className="fas fa-paper-plane me-1"></i>
                  <span className="d-none d-md-inline">Enviar</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        <div className='text-muted fs-8 mt-2 px-3'>
          Pressione Enter para enviar, Shift+Enter para nova linha
        </div>
      </div>
      
      {/* Componente de teste temporário - OCULTO */}
      {/* <VoiceTestButton /> */}
    </div>
  )
}

// Componente para cada mensagem
interface MessageBubbleProps {
  message: Message
  isAgent: boolean
  hotelId: number
  agentName: string
  roomNumber: string
  showTime: boolean
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isAgent, hotelId, agentName, roomNumber, showTime }) => {
  // 🔧 FIX: Validar timestamp antes de formatar
  let timeAgo = 'agora'
  try {
    const timestamp = message.created || (message as any).timestamp || new Date().toISOString()
    const date = new Date(timestamp)
    
    // Verificar se a data é válida
    if (!isNaN(date.getTime())) {
      timeAgo = formatDistanceToNow(date, {
        locale: ptBR,
        addSuffix: true
      })
    } else {
      console.warn('⚠️ Timestamp inválido na mensagem:', message)
    }
  } catch (error) {
    console.error('❌ Erro ao formatar timestamp:', error, message)
  }
  
  return (
    <div className='mb-4'>
      {showTime && (
        <div className='text-center mb-4'>
          <span className='badge badge-light-primary fs-8'>
            {timeAgo}
          </span>
        </div>
      )}
      
      <div className={`d-flex ${isAgent ? 'justify-content-end' : 'justify-content-start'}`}>
        <div className={`d-flex flex-column ${isAgent ? 'align-items-end' : 'align-items-start'}`}>
          <div
            className={`p-3 rounded max-w-400px ${
              isAgent
                ? 'bg-primary text-white'
                : 'bg-light-info text-dark'
            }`}
            style={{ 
              maxWidth: '400px',
              wordWrap: 'break-word'
            }}
          >
            {/* Imagem se houver */}
            {message.imageUrl ? (
              <div className='mb-2'>
                <MessageImage 
                  imageUrl={message.imageUrl}
                  alt='Anexo da mensagem'
                />
              </div>
            ) : null}
            
            {/* Info do hotel - Mostrar sempre em desenvolvimento */}
            {process.env.NODE_ENV === 'development' && (
              <div className="small text-muted mb-1">
                {isAgent ? (
                  // Mensagem do Hotel (agente)
                  <>Hotel ID {hotelId} - {agentName}{message.imageUrl ? ' • ✅ Imagem anexada' : ''}</>
                ) : (
                  // Mensagem do Hóspede
                  <>Quarto - {roomNumber}{message.imageUrl ? ' • ✅ Imagem anexada' : ''}</>
                )}
              </div>
            )}
            
            {/* Texto da mensagem */}
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {message.text}
            </div>
          </div>
          
          {/* Status e timestamp */}
          <div className={`text-muted fs-8 mt-1 ${isAgent ? 'text-end' : 'text-start'}`}>
            <span>{new Date(message.created).toLocaleTimeString('pt-BR', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}</span>
            
            {isAgent && (
              <span className='ms-2'>
                {message.deliveryStatus === 'sent' && '✓'}
                {message.deliveryStatus === 'delivered' && '✓✓'}
                {message.deliveryStatus === 'read' && <span className='text-primary'>✓✓</span>}
                {message.deliveryStatus === 'failed' && <span className='text-danger'>✗</span>}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MessageView
